import { Injectable } from '@angular/core';

import { Action } from '@ngrx/store';

import { BaseBetslipGroupPick } from '../../../../core/picks/betslip-group-pick';
import { BetslipPick } from '../../../../core/picks/betslip-pick';
import { BetslipV1Pick } from '../../../../core/picks/betslip-v1-pick';
import { BetslipV2Pick } from '../../../../core/picks/betslip-v2-pick';
import { PickUpdateType } from '../../../../core/picks/pick-models';
import { IPlacedBetslipResult } from '../../../betplacement/models';
import { isFailedSlip } from '../../../betplacement/utils';
import BetslipPickActions from '../../../picks/actions';
import { BetslipErrorPickUpdaterService } from './betslip-error-pick-updater.service';

@Injectable({ providedIn: 'root' })
export class BetslipErrorBetslipUpdaterService {
    constructor(private betslipErrorPickUpdaterService: BetslipErrorPickUpdaterService) {}

    getUpdateEventsFromPlaceBet(result: IPlacedBetslipResult, picks: BetslipPick[]): Action[] {
        const actions: Action[] = [];
        let previousPicks: BetslipPick[] = [];

        for (const slip of result.slips.filter(isFailedSlip)) {
            const pickErrorPicksToUpdate = this.betslipErrorPickUpdaterService.getUpdatedPicks(slip);

            const picksToUpdate = this.concatPicksArrayWithDistinction(picks, pickErrorPicksToUpdate, previousPicks);

            // eslint-disable-next-line no-bitwise
            const updateType: PickUpdateType = PickUpdateType.MarketVisibility | PickUpdateType.Odds;

            const addUpdatePickActionFor = <T extends BetslipPick>(
                predicate: (pick: BetslipPick) => pick is T,
                actionCreator: (_: { pickUpdates: { pick: T; updateType: PickUpdateType }[]; byPassErrorClear: boolean }) => Action,
            ) => {
                const updates = picksToUpdate.filter(predicate).map((pick) => ({ pick, updateType }));
                if (updates.length) {
                    actions.push(actionCreator({ pickUpdates: updates, byPassErrorClear: true }));
                }
            };
            const pickBelongsToGroup = (pick: BetslipPick) =>
                picksToUpdate.filter<BaseBetslipGroupPick>(BaseBetslipGroupPick.isPick).some((t) => t.hasPick(pick.id));
            addUpdatePickActionFor(
                (pick): pick is BetslipV1Pick => pick instanceof BetslipV1Pick && !pickBelongsToGroup(pick),
                BetslipPickActions.updateV1Picks,
            );
            addUpdatePickActionFor(
                (pick): pick is BetslipV2Pick => pick instanceof BetslipV2Pick && !pickBelongsToGroup(pick),
                BetslipPickActions.updateV2Picks,
            );
            addUpdatePickActionFor((pick): pick is BaseBetslipGroupPick => pick instanceof BaseBetslipGroupPick, BetslipPickActions.updateGroupPicks);
            addUpdatePickActionFor((pick): pick is BetslipPick => pickBelongsToGroup(pick), BetslipPickActions.updateGroupPickLegs);

            previousPicks = [...previousPicks, ...picksToUpdate];
        }

        return actions;
    }

    private concatPicksArrayWithDistinction(sourcePicks: BetslipPick[], addedPicks: BetslipPick[], previousPicks: BetslipPick[]): BetslipPick[] {
        const addedPickIds = addedPicks.map((pick) => pick.id);
        const sourcePicksWithoutAddedPicks = sourcePicks.filter(
            (sourcePick) =>
                addedPickIds.every((addedPick) => !addedPick.isEqual(sourcePick.id)) &&
                previousPicks.every((previousPick) => !previousPick.id.isEqual(sourcePick.id)),
        );

        return [...sourcePicksWithoutAddedPicks, ...addedPicks];
    }
}
